import React from 'react'
import clsx from 'clsx'
import { debounce } from 'lodash'
import { Fade, Menu, MenuItem } from '@mui/material'

import { ChannelOptionsType } from '..'
import { postUser, searchUser } from '../../../../../lib/api'
import { useAppDispatch } from '../../../../../hooks/redux'
import { AddErrorAlert } from '../../../../../slices/alert'
import { DEBOUNCE_DELAY } from '../../../../../hooks/useDelayedUserSearch'
import { MIN_CHAR_COUNT } from '../../../../../hooks/useDelayedUserSearch/v2'
import { useEnvironment } from '../../../../../providers/EnvironmentProvider'

import EmailChip from './Chip'

import useStyles from './styles'
import Tooltip from '../../../../atoms/Tooltip'
import RemoveAgentIcon from '../../../ChatWindowV2/Header/TransferModal/Icon'

const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

interface EmailInputProps {
  subject: string
  setSubject: (val: string) => void
  users: Array<any>
  setUsers: (val: any) => void
  type: string
  createUser: (
    value: string,
    name: string,
    method: ChannelOptionsType
  ) => object
  creatingEmail: boolean
  setCreatingEmail: (val: boolean) => void
  isMedium: boolean
}

const EmailInput = (
  {
    subject,
    setSubject,
    users,
    setUsers,
    type,
    createUser,
    creatingEmail,
    setCreatingEmail,
    isMedium
  }: EmailInputProps,

  ref
) => {
  const dispatch = useAppDispatch()
  const { getEnvValue } = useEnvironment()
  const [searchVal, setSearchVal] = React.useState('')
  const [value, setVal] = React.useState('')
  const [suggestions, setSuggestions] = React.useState([])
  const [menuOpen, setMenuOpen] = React.useState(false)
  const [noResults, setNoResults] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [focused, setFocused] = React.useState(false)
  const [newUser, setNewUser] = React.useState('')
  const anchorRef = React.useRef<HTMLDivElement>()
  const newEmailRef = React.useRef<HTMLInputElement>()

  const classes = useStyles()

  const saveNewUser = React.useCallback(
    async formatted => {
      try {
        const userToSave = createUser(formatted, newUser, 'Email') as any
        userToSave.type_id =
          getEnvValue('config.users', 'u_default_external_type_id', null) ??
          getEnvValue('config.users', 'users_default_external_type_id', null)
        const user = await postUser(userToSave)
        const channel = user.channels[0]
        //add new user to recipents
        setUsers(old => {
          return [...old, { user, channel }]
        })
        //cleanup
        setCreatingEmail(false)
        setNewUser('')
        setVal('')
      } catch (err) {
        dispatch(AddErrorAlert({ err }))
      }
    },
    [dispatch, createUser, getEnvValue, newUser, setUsers, setCreatingEmail]
  )

  // Fetches and stores users and data
  const handleSearch = React.useCallback(
    async chars => {
      try {
        setNoResults(false)
        setLoading(true)
        const results = await searchUser(chars, [type])
        const values = []
        setNoResults(results.length === 0)
        results.forEach(user => {
          user.channels.forEach(channel => {
            if (channel.type === type && channel.value) {
              values.push({ user, channel })
            }
          })
        })

        const sorted = values.sort((a, b) => {
          const aName = a?.user?.name
          const bName = b?.user?.name
          return aName.localeCompare(bName)
        })
        setLoading(false)
        setSuggestions(sorted)
      } catch (err) {
        setLoading(false)
        dispatch(AddErrorAlert({ err }))
      }
    },
    [dispatch, type]
  )

  const validateData = React.useCallback(() => {
    const valid = EMAIL_REGEX.test(value)
    if (newUser === '') {
      dispatch(
        AddErrorAlert({
          err: { message: `Error creating new contact: Add contact name` }
        })
      )
      return false
    }
    if (!valid) {
      dispatch(
        AddErrorAlert({
          err: {
            message: `Error creating new contact: Enter a valid email address`
          }
        })
      )
      return false
    }
    return true
  }, [dispatch, newUser, value])

  const handleKeyDown = React.useCallback(
    async e => {
      if (e.keyCode === 13) {
        const valid = validateData()
        if (valid) {
          saveNewUser(value)
        }
      }
    },
    [value, saveNewUser, validateData]
  )

  const handleBlur = React.useCallback(() => {
    const valid = validateData()
    if (valid) {
      saveNewUser(value)
    }
    setFocused(false)
  }, [value, saveNewUser, validateData])

  const handleOnRemoveClick = React.useCallback(
    (user, channel) => {
      setUsers(old => {
        const indexForRemoval = old.findIndex(
          entry => entry.user.id === user.id && entry.channel.id === channel.id
        )
        const copy = [...old]
        const result = copy
          .slice(0, indexForRemoval)
          .concat(copy.slice(indexForRemoval + 1))
        return result
      })
      if (ref.current) {
        setTimeout(() => {
          ref.current.focus()
        }, 0)
      }
    },
    [setUsers, ref]
  )

  //eslint-disable-next-line
  const delayedSearch = React.useCallback(
    debounce(handleSearch, DEBOUNCE_DELAY, {
      leading: true
    }),
    []
  )

  const handleOnSearchChange = React.useCallback(
    e => {
      const val = e.target.value
      if (val.length > MIN_CHAR_COUNT) {
        delayedSearch(val)
      } else {
        setSuggestions([])
      }
      setSearchVal(val)
    },
    [delayedSearch]
  )

  const showResults = React.useMemo(
    () => searchVal.length > MIN_CHAR_COUNT,
    [searchVal.length]
  )

  React.useEffect(() => {
    setMenuOpen(showResults)
    if (showResults) {
      // Refocus input
      if (ref.current) {
        setTimeout(() => {
          ref.current.focus()
        }, 0)
      }
    }
  }, [showResults, ref])

  return (
    <div className={classes.root}>
      <div className={classes.sectionRoot} ref={anchorRef}>
        <label className={classes.sectionLabels}>To</label>
        <div className={classes.chipsSection}>
          {users.map(data => {
            const { user, channel } = data
            const emailChannels = user.channels.filter(c => c.type === 'email')
            return (
              <EmailChip
                key={`${user.id}-${channel.id}-selected-email`}
                user={user}
                channel={channel}
                emailChannels={emailChannels}
                handleOnRemoveClick={handleOnRemoveClick}
                setUsers={setUsers}
              />
            )
          })}
          {creatingEmail && (
            <div className={classes.chip}>
              <label className={classes.addLabel}>
                {isMedium ? 'Add' : 'Add Contact'}
              </label>
              "
              <label
                className={classes.newContactName}
              >{`${newUser} : `}</label>
              {/* lets use label for now, see it needs to be changed. */}
              {/* <input
                value={newUser}
                onChange={e => {
                  const val = e.target.value
                  setNewUser(val)
                }}
                className={clsx(classes.transparentInput)}
              /> */}
              <input
                ref={newEmailRef}
                value={value}
                onChange={e => {
                  const val = e.target.value
                  setVal(val)
                }}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                onFocus={() => setFocused(true)}
                placeholder='enter email address'
                className={clsx(classes.transparentInput)}
              />
              {!focused && (
                <Tooltip title='Remove from list' arrow>
                  <span style={{ display: 'flex' }}>
                    <RemoveAgentIcon
                      onClick={() => {
                        setNewUser('')
                        setVal('')
                        setCreatingEmail(false)
                      }}
                    />
                  </span>
                </Tooltip>
              )}
            </div>
          )}
          <input
            className={classes.input}
            value={searchVal}
            onChange={handleOnSearchChange}
            ref={ref}
            placeholder='Type to search or add new contact ...'
          />
        </div>
      </div>

      <div className={classes.sectionRoot} onClick={e => e.stopPropagation()}>
        <label className={classes.sectionLabels}>Subject</label>

        <input
          className={classes.input}
          value={subject}
          onChange={e => setSubject(e.target.value)}
        />
      </div>
      {menuOpen && (
        <Menu
          open={menuOpen}
          disableEnforceFocus
          anchorEl={anchorRef.current}
          anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
          transformOrigin={{ horizontal: 'center', vertical: 4 }}
          TransitionComponent={Fade}
          classes={{ paper: classes.resultsContainer }}
          PaperProps={{
            style: {
              width: `${
                anchorRef.current?.getBoundingClientRect()?.width ?? 0
              }px`
            }
          }}
        >
          {loading && (
            <div className={classes.noResults}>Searching contacts...</div>
          )}
          {noResults && (
            <div className={classes.noResults}>No match result found</div>
          )}
          <div
            className={classes.newContact}
            onClick={() => {
              //Begin creating new contact
              setCreatingEmail(true)
              setNewUser(searchVal)
              // Reset state
              setSearchVal('')
              setMenuOpen(false)
              setSuggestions([])

              //select input
              setTimeout(() => {
                if (newEmailRef?.current) {
                  newEmailRef.current.focus()
                }
              }, 1)
            }}
          >
            + Add
            <strong>{searchVal}</strong>
            to new contact
          </div>
          <div>
            {suggestions.map(result => {
              const { channel, user } = result
              return (
                <MenuItem
                  key={`${user.id}-${channel.id}`}
                  component='div'
                  className={classes.result}
                  onClick={() => {
                    setUsers(old => {
                      const alreadyExists = old.some(
                        entry =>
                          entry.user.id === user.id &&
                          entry.channel.id === channel.id
                      )
                      if (alreadyExists) {
                        return old
                      }
                      return [...old, { user, channel }]
                    })
                    // Reset state
                    setSearchVal('')
                    setMenuOpen(false)
                    setSuggestions([])
                  }}
                  disableGutters
                >
                  <div>
                    <div>{user?.name}</div>
                    <div className={classes.emailResult}>{channel?.value}</div>
                  </div>
                </MenuItem>
              )
            })}
          </div>
        </Menu>
      )}
    </div>
  )
}

export default React.forwardRef(EmailInput)
